import { Slot } from '@radix-ui/react-slot'
import type { ComponentProps } from 'react'
import { ChevronRightIcon, DotsHorizontalIcon } from '@/components/ui/icons'
import { cn } from '@/helpers/utils'

function Breadcrumb(props: ComponentProps<'nav'>) {
	return <nav aria-label='breadcrumb' data-slot='breadcrumb' {...props} />
}

function BreadcrumbList({ className, ...props }: ComponentProps<'ol'>) {
	return (
		<ol
			className={cn(
				'flex flex-wrap items-center gap-1 break-words text-muted-foreground text-sm',
				className
			)}
			data-slot='breadcrumb-list'
			{...props}
		/>
	)
}

function BreadcrumbItem({ className, ...props }: ComponentProps<'li'>) {
	return (
		<li
			className={cn('inline-flex items-center gap-1.5', className)}
			data-slot='breadcrumb-item'
			{...props}
		/>
	)
}

function BreadcrumbLink({
	asChild,
	className,
	...props
}: ComponentProps<'a'> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'a'

	return (
		<Comp
			className={cn('transition-colors hover:text-foreground', className)}
			data-slot='breadcrumb-link'
			{...props}
		/>
	)
}

function BreadcrumbPage({ className, ...props }: ComponentProps<'span'>) {
	return (
		<span
			aria-current='page'
			className={cn('font-normal text-foreground', className)}
			data-slot='breadcrumb-page'
			{...props}
		/>
	)
}

function BreadcrumbSeparator({
	children,
	className,
	...props
}: ComponentProps<'li'>) {
	return (
		<li
			aria-hidden='true'
			className={cn('[&>svg]:size-3.5', className)}
			data-slot='breadcrumb-separator'
			role='presentation'
			{...props}
		>
			{children ?? <ChevronRightIcon />}
		</li>
	)
}

function BreadcrumbEllipsis({ className, ...props }: ComponentProps<'span'>) {
	return (
		<span
			aria-hidden='true'
			className={cn('flex size-9 items-center justify-center', className)}
			data-slot='breadcrumb-ellipsis'
			role='presentation'
			{...props}
		>
			<DotsHorizontalIcon className='size-4' />
			<span className='sr-only'>More</span>
		</span>
	)
}

export {
	Breadcrumb,
	BreadcrumbEllipsis,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
}
